import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormHelperText,
  TextField,
  Button,
} from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';
import useProjectsAsync from 'store/useProjectsAsync';
import IProject from 'types/IProject';

interface StartWorkerDialogProps {
  open: boolean;
  nodeAgentId: string | null;
  workdirPackages: Record<string, string>;
  onClose: () => void;
  onSubmit: () => void;
}

export default function StartWorkerDialog({
  open,
  nodeAgentId,
  workdirPackages,
  onClose,
  onSubmit,
}: StartWorkerDialogProps) {
  const [packageName, setPackageName] = useState('pytailor');
  const [ncores, setNcores] = useState('1');
  const [sleep, setSleep] = useState('3');
  const [workerName, setWorkerName] = useState('');
  const [workdirName, setWorkdirName] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<IProject[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  // Track the Autocomplete's input so a capability the user typed but didn't
  // commit with Enter/blur before clicking Start still gets included.
  const [capabilityInput, setCapabilityInput] = useState('');
  const [pythonVersion, setPythonVersion] = useState('');
  const [batch, setBatch] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPackageName('pytailor');
      setNcores('1');
      setSleep('3');
      setWorkerName('');
      setWorkdirName('');
      setSelectedProjects([]);
      setCapabilities([]);
      setCapabilityInput('');
      setPythonVersion('');
      setBatch(true);
      setIsSubmitting(false);
      setError(null);
    }
  }, [open]);

  const existingWorkdirs = Object.keys(workdirPackages);
  const lockedPackage = workdirPackages[workdirName] ?? null;

  const projectsAsync = useProjectsAsync();
  const projects = projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH' ? projectsAsync.data : [];

  const handleSubmit = async () => {
    if (!nodeAgentId || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    const pendingCap = capabilityInput.trim();
    const finalCapabilities = pendingCap && !capabilities.includes(pendingCap) ? [...capabilities, pendingCap] : capabilities;
    try {
      await sendNodeCommand(nodeAgentId, 'start_worker', {
        package_name: lockedPackage ?? packageName,
        ncores: parseInt(ncores, 10) || 1,
        sleep: parseFloat(sleep) || 3,
        worker_name: workerName,
        ...(workdirName && { workdir_name: workdirName }),
        ...(selectedProjects.length > 0 && {
          project_id_filter: selectedProjects.map((p) => p.id),
        }),
        ...(finalCapabilities.length > 0 && { capabilities: finalCapabilities }),
        ...(pythonVersion.trim() && { python_version: pythonVersion.trim() }),
        ...(batch && { batch: true }),
      });
      onSubmit();
    } catch (err) {
      console.error('Failed to start worker:', err);
      setError('Failed to start worker. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start Worker</DialogTitle>
      <DialogContent>
        <TextField
          label="Package Name"
          value={lockedPackage ?? packageName}
          onChange={(e) => setPackageName(e.target.value)}
          fullWidth
          margin="normal"
          disabled={lockedPackage !== null}
          helperText={
            lockedPackage !== null
              ? 'Locked to the package installed in the selected working directory.'
              : 'Supports version pinning, e.g. pytailor==0.11.0'
          }
        />
        <TextField
          label="Python Version (optional)"
          value={pythonVersion}
          onChange={(e) => setPythonVersion(e.target.value)}
          fullWidth
          margin="normal"
          disabled={lockedPackage !== null}
          placeholder="e.g. 3.11"
          helperText={
            lockedPackage !== null
              ? 'Locked when reusing an existing working directory.'
              : "Leave empty to use the node agent's own Python."
          }
        />
        <TextField
          label="Number of Cores"
          type="number"
          value={ncores}
          onChange={(e) => setNcores(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Maximum tasks this worker will run in parallel."
        />
        <TextField
          label="Sleep Interval (seconds)"
          type="number"
          value={sleep}
          onChange={(e) => setSleep(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Seconds the worker waits between check-ins to the server."
        />
        <TextField
          label="Worker Name"
          required
          value={workerName}
          onChange={(e) => setWorkerName(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Identifies this worker. A workflow can be restricted to a worker name so only matching workers pick up its tasks."
        />
        <Autocomplete
          freeSolo
          options={existingWorkdirs}
          value={workdirName}
          onChange={(_, value) => {
            const name = (value as string) || '';
            setWorkdirName(name);
            const pkg = workdirPackages[name];
            if (pkg) setPackageName(pkg);
          }}
          onInputChange={(_, value) => {
            setWorkdirName(value);
            const pkg = workdirPackages[value];
            if (pkg) setPackageName(pkg);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Working Directory (optional)"
              margin="normal"
              helperText="Leave empty for a fresh environment. Select an existing name to reuse its virtual environment."
            />
          )}
        />
        <Autocomplete
          multiple
          options={projects}
          getOptionLabel={(option) => option.name}
          value={selectedProjects}
          onChange={(_, value) => setSelectedProjects(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Filters (optional)"
              margin="normal"
              helperText="Only pick up tasks from the selected projects. Leave empty to accept any project."
            />
          )}
        />
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={capabilities}
          onChange={(_, value) => setCapabilities(value as string[])}
          inputValue={capabilityInput}
          onInputChange={(_, value) => setCapabilityInput(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Capabilities (optional)"
              margin="normal"
              helperText="Tags this worker provides. A task that requires a capability will only run on a worker that has it. Type and press Enter to add."
            />
          )}
        />
        <FormControlLabel
          control={<Checkbox checked={batch} onChange={(e) => setBatch(e.target.checked)} />}
          label="Batch task check-ins"
          sx={{ mt: 1, mb: 0 }}
        />
        <FormHelperText sx={{ ml: 4, mt: 0 }}>
          Bundles task updates into the periodic worker check-in instead of sending them individually. Recommended for
          large/high load workers (requires pytailor&gt;=0.13.0).
        </FormHelperText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!workerName.trim() || isSubmitting}
        >
          {isSubmitting ? 'Starting\u2026' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
